/**
 * Small-screen table chrome: the sticky header clone, the floating tier label and tier
 * IntersectionObserver, and the scroll-snap padding for the pinned columns.
 *
 * Calls on sibling mixins via `this`: Filtering (isVisiblePlayerRow, isTierRowVisible,
 * getFirstVisibleTier), Sorting (getTierSortValueKey), GuestCta (syncGuestCtaViewportWidth),
 * UserLeague (selectedUserLeagueId).
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';

export default () => ({
    // Mobile sticky tier row
    currentVisibleTier: '',
    showMobileTierLabel: false,
    mobileStickyTierLabel: null,

    // Tier observer and sticky-clone bookkeeping, created lazily by the methods below
    tierObserver: null,
    _tierElementsAtTop: null,
    _mobileStickyHeaderHeight: null,
    _floatingTierScrollBound: false,
    _resizeObserver: null,
    _theadObserver: null,
    _scrollSyncAbort: null,

    initMobileTableChrome() {
        this.updateShowMobileTierLabel();
        this.bindFloatingTierScrollSync();
    },

    // Runs after the first refreshSections pass so visibleTiers is populated
    initCurrentVisibleTier() {
        // Floating tier label in the sticky clone takes over while scrolling
        if (this.isSmallScreen) {
            this.currentVisibleTier = this.getFirstVisibleTier();
        }
    },

    refreshMobileTableChrome() {
        this.setupMobileStickyHeader();
        this.syncScrollSnapPadding();
        // Runs synchronously in the htmx:afterSwap handler, so the freshly swapped
        // CTA element gets its width/left vars before the browser paints.
        this.syncGuestCtaViewportWidth();
        this.syncCurrentVisibleTier();
    },

    getMobileStickyHeaderHeight() {
        return this._mobileStickyHeaderHeight ?? 0;
    },

    setMobileStickyHeaderHeight(height, container) {
        this._mobileStickyHeaderHeight = height;
        container?.style.setProperty('--rankings-mobile-sticky-header-height', `${height}px`);
    },

    pickHighestTopTierElement(elements, boundary) {
        let highestTopElement = null;
        let highestTop = -Infinity;
        for (const el of elements) {
            const currentTop = el.getBoundingClientRect().top;
            if (currentTop <= boundary && currentTop >= highestTop) {
                highestTop = currentTop;
                highestTopElement = el;
            }
        }
        return highestTopElement;
    },

    updateShowMobileTierLabel() {
        this.showMobileTierLabel = this.usesFloatingMobileTierLabel();

        document
            .querySelector('#rankingsTableContainer')
            ?.classList.toggle('rankings-table-container--floating-tier-label', this.showMobileTierLabel);

        this.syncCurrentVisibleTier();
    },

    usesFloatingMobileTierLabel() {
        return (
            this.isSmallScreen &&
            this.basePath !== 'weekly-rankings' &&
            this.sortColumn.includes('dsValue') &&
            this.selectedUserLeagueId === null
        );
    },

    setMobileStickyCloneActive(isActive) {
        const container = document.querySelector('#rankingsTableContainer');
        if (!container) {
            return;
        }
        const className = 'is-mobile-sticky-clone-active';
        if (container.classList.contains(className) === isActive) {
            return;
        }
        container.classList.toggle(className, isActive);
        if (this.isSmallScreen) {
            Alpine.nextTick(() => {
                this.syncCurrentVisibleTier();
                if (!this.showMobileTierLabel) {
                    this.setupTierObserver();
                }
            });
        }
    },

    bindFloatingTierScrollSync() {
        if (this._floatingTierScrollBound) {
            return;
        }
        this._floatingTierScrollBound = true;
        window.addEventListener('scroll', () => this.syncCurrentVisibleTier(), { passive: true });
    },

    syncCurrentVisibleTier() {
        if (this.showMobileTierLabel) {
            this.syncCurrentVisibleTierFromPlayers();
        }
    },

    syncCurrentVisibleTierFromPlayers() {
        if (!this.showMobileTierLabel || !this.isSmallScreen) {
            return;
        }

        const headerOffset = this.getMobileStickyHeaderHeight();
        const players = [...document.querySelectorAll('#rankingsTable tbody[data-player-row]')].filter(
            row => this.isVisiblePlayerRow(row) && !row.classList.contains('search-hidden')
        );

        if (!players.length) {
            return;
        }

        let tier = '';
        for (const row of players) {
            const rect = row.getBoundingClientRect();
            if (rect.bottom <= headerOffset + 1) {
                continue;
            }
            tier = this.getPlayerRowTier(row);
            break;
        }

        if (!tier) {
            const lastAbove = [...players].reverse().find(row => row.getBoundingClientRect().top < headerOffset);
            if (lastAbove) {
                tier = this.getPlayerRowTier(lastAbove);
            }
        }

        if (tier) {
            this.currentVisibleTier = tier;
        }
    },

    observeVisibleTierRows() {
        if (!this.tierObserver || !this.isSmallScreen) {
            return;
        }
        document.querySelectorAll('tbody[data-tier-row]').forEach(tierRow => {
            this.tierObserver.unobserve(tierRow);
            if (this.isTierRowVisible(tierRow)) {
                this.tierObserver.observe(tierRow);
            }
        });
    },

    setupTierObserver() {
        if (!this.isSmallScreen) {
            if (this.tierObserver) {
                this.tierObserver.disconnect();
                this.tierObserver = null;
            }
            return;
        }

        if (this.showMobileTierLabel) {
            if (this.tierObserver) {
                this.tierObserver.disconnect();
                this.tierObserver = null;
            }
            this.syncCurrentVisibleTier();
            return;
        }

        if (!this._tierElementsAtTop) {
            this._tierElementsAtTop = new Set();
        }

        if (this.tierObserver) {
            this.tierObserver.disconnect();
        }

        const headerOffset = this.getMobileStickyHeaderHeight();
        const elementsAtTop = this._tierElementsAtTop;

        this.tierObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    const sectionRect = entry.target.getBoundingClientRect();
                    if (!entry.isIntersecting && sectionRect.top <= headerOffset) {
                        elementsAtTop.add(entry.target);
                    } else if (entry.isIntersecting && sectionRect.top > headerOffset) {
                        elementsAtTop.delete(entry.target);
                    }
                });

                const highestTopElement = this.pickHighestTopTierElement(elementsAtTop, headerOffset);
                if (highestTopElement) {
                    this.currentVisibleTier = highestTopElement.dataset.tierNumber || '';
                }
            },
            {
                root: null,
                threshold: [0, 1],
                rootMargin: `-${headerOffset}px 0px 0px 0px`,
            }
        );

        this.observeVisibleTierRows();
    },

    teardownMobileStickyClone(mobileStickyTable, mobileStickyTableContainer, mainTableHead) {
        if (!mobileStickyTable?.hasChildNodes()) {
            return;
        }
        mobileStickyTable.innerHTML = '';
        mobileStickyTableContainer.style.marginBottom = '';
        this.setMobileStickyHeaderHeight(0, document.querySelector('#rankingsTableContainer'));
        this.setMobileStickyCloneActive(false);
        if (this.mobileStickyTierLabel && mainTableHead && !mainTableHead.contains(this.mobileStickyTierLabel)) {
            mainTableHead.appendChild(this.mobileStickyTierLabel);
        }
        if (this._scrollSyncAbort) {
            this._scrollSyncAbort.abort('scroll-aborted');
            this._scrollSyncAbort = null;
        }
    },

    measurePinnedLeftWidth(cells) {
        let sum = 0;
        for (const cell of cells) {
            const position = getComputedStyle(cell).position;
            if (position !== 'sticky' && position !== '-webkit-sticky') {
                break;
            }
            sum += cell.getBoundingClientRect().width;
        }
        return sum;
    },

    syncScrollSnapPadding() {
        const scrollContainer = document.querySelector('.main-ranking-div');
        const table = document.querySelector('#rankingsTable');
        if (!scrollContainer || !table) {
            return;
        }

        const headerRow = table.querySelector('thead tr:not(.ds-table-divider-row)');
        const bodyRow = table.querySelector('tbody[data-player-row] tr.player-row');
        const headerWidth = headerRow ? this.measurePinnedLeftWidth(headerRow.children) : 0;
        const bodyWidth = bodyRow ? this.measurePinnedLeftWidth(bodyRow.children) : 0;
        const padding = Math.ceil(Math.max(headerWidth, bodyWidth));

        scrollContainer.style.scrollPaddingLeft = `${padding}px`;
    },

    setupMobileStickyHeader() {
        const mobileStickyTableContainer = document.querySelector('#mobile-header-sticky');
        if (!mobileStickyTableContainer) {
            return;
        }

        this.mobileStickyTierLabel = document.querySelector('#mobile-sticky-tier-label');
        const mobileStickyTable = mobileStickyTableContainer.querySelector('table');
        const mainTableHead = document.querySelector('#rankingsTable thead');

        // Mobile clone header below desktop breakpoint (1024px), same as isSmallScreen.
        if (!this.isSmallScreen) {
            mobileStickyTableContainer.style.display = 'none';
            if (this.mobileStickyTierLabel) {
                this.mobileStickyTierLabel.style.display = 'none';
            }
            this.teardownMobileStickyClone(mobileStickyTable, mobileStickyTableContainer, mainTableHead);
            this.syncScrollSnapPadding();
            return;
        }

        mobileStickyTableContainer.style.display = '';
        if (this.mobileStickyTierLabel) {
            this.mobileStickyTierLabel.style.display = '';
        }
        const mainTableContainer = document.querySelector('#table-container');
        const mainTable = document.querySelector('#rankingsTable');
        if (!mainTableContainer || !mainTableHead || !mainTable || !mobileStickyTable) {
            return;
        }

        this.teardownMobileStickyClone(mobileStickyTable, mobileStickyTableContainer, mainTableHead);

        // Create a resize observer to adjust the sticky header's margin when its size changes
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }

        const rankingsContainer = document.querySelector('#rankingsTableContainer');

        this._resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                if (entry.target === mobileStickyTableContainer && this.isSmallScreen) {
                    const height = entry.contentRect.height;
                    mobileStickyTableContainer.style.marginBottom = height > 0 ? `${-1 * height}px` : '';
                    this.setMobileStickyHeaderHeight(height, rankingsContainer);
                }
            }
        });

        // Start observing the sticky header container
        this._resizeObserver.observe(mobileStickyTableContainer);

        // Remove old observer
        if (this._theadObserver) {
            this._theadObserver.disconnect();
            this._theadObserver = null;
        }

        // Remove old scroll sync abort controller
        if (this._scrollSyncAbort) {
            this._scrollSyncAbort.abort('scroll-aborted');
            this._scrollSyncAbort = null;
        }

        this._theadObserver = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) {
                        if (!mobileStickyTable.hasChildNodes()) {
                            const stickyHeader = document.createElement('thead');
                            stickyHeader.classList.add('sticky-header');

                            const stickyHeaderMainRow = document.createElement('tr');
                            let stickyHeaderGroupRow = null;

                            if (this.researchDepth === 'projections') {
                                stickyHeaderMainRow.classList.add('positional-projections-expand-row');
                                stickyHeaderGroupRow = document.createElement('tr');
                                Array.from(
                                    mainTableHead.querySelectorAll('tr:not(.positional-projections-expand-row)')[0].children
                                ).forEach(tierRow => {
                                    stickyHeaderGroupRow.appendChild(tierRow.cloneNode(true));
                                });
                            }

                            const mainHeaderRow = mainTableHead.querySelector('tr:not(.ds-table-divider-row)');

                            Array.from(mainHeaderRow.children).forEach(child => {
                                const cellClone = child.cloneNode(true);
                                const widthPx = `${child.getBoundingClientRect().width}px`;
                                cellClone.style.width = widthPx;
                                cellClone.style.minWidth = widthPx;
                                cellClone.style.maxWidth = widthPx;
                                stickyHeaderMainRow.appendChild(cellClone);
                            });

                            mobileStickyTable.style.tableLayout = 'fixed';
                            mobileStickyTable.style.width = `${mainTable.getBoundingClientRect().width}px`;

                            stickyHeader.appendChild(stickyHeaderMainRow);

                            if (stickyHeaderGroupRow) {
                                stickyHeader.appendChild(stickyHeaderGroupRow);
                            }
                            if (this.showMobileTierLabel && this.mobileStickyTierLabel) {
                                const tierLabelCell = this.mobileStickyTierLabel.querySelector('.tier-label');
                                if (tierLabelCell) {
                                    tierLabelCell.colSpan = mainHeaderRow.children.length;
                                }
                                stickyHeader.appendChild(this.mobileStickyTierLabel);
                            }

                            mobileStickyTable.appendChild(stickyHeader);
                            this.setMobileStickyCloneActive(true);

                            let ticking = false;
                            this._scrollSyncAbort = new AbortController();

                            mainTableContainer.addEventListener(
                                'scroll',
                                e => {
                                    if (ticking) return;
                                    requestAnimationFrame(() => {
                                        mobileStickyTableContainer.scrollLeft = e.target.scrollLeft;
                                        ticking = false;
                                    });
                                    ticking = true;
                                },
                                { signal: this._scrollSyncAbort.signal }
                            );
                        }
                    } else {
                        this.teardownMobileStickyClone(mobileStickyTable, mobileStickyTableContainer, mainTableHead);
                    }
                });
            },
            { root: null, threshold: [0, 0.5, 1] }
        );
        this._theadObserver.observe(mainTableHead);
        this.syncScrollSnapPadding();
    },
});
